import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { GatewayConnectDialogs } from '../components/GatewayConnectDialogs'
import { useGatewayConnect } from '../hooks/use-gateway-connect'
import { pendingConnectFromParams } from '../utils/gateway-connect-params'
import { finishOAuthPickerStep } from '../utils/oauth-popup-complete'

function leaveForErrorPage(error: string, description: string) {
  const params = new URLSearchParams({ error, description, appUrl: window.location.origin })
  window.location.replace(`/oauth-callback-error.html?${params.toString()}`)
}

/**
 * Finishes a partner-gateway connect inside the OAuth popup. When routing
 * sends a Facebook/Instagram business to Composio, `/v1/auth/:provider` sends
 * the popup to Composio's consent, which returns here. Success and failure
 * both hand off to the static callback pages, which signal the opener the same
 * way a direct connect does.
 */
export function GatewayConnectCompletePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const ctx = useMemo(() => pendingConnectFromParams(params), [params])

  // The first outcome wins: ConfirmDialog closes itself (a "cancel") right
  // after a successful switch-account confirm.
  const finished = useRef(false)
  const finishOnce = useCallback((leave: () => void) => {
    if (finished.current) return
    finished.current = true
    leave()
  }, [])

  const handleFailed = useCallback(
    () =>
      finishOnce(() =>
        leaveForErrorPage('Connection Error', 'We could not finish connecting. Please try again.')
      ),
    [finishOnce]
  )
  const handleCancelled = useCallback(
    () =>
      finishOnce(() => leaveForErrorPage('Connection Cancelled', 'The connection was cancelled.')),
    [finishOnce]
  )

  const connect = useGatewayConnect({
    onConnected: () =>
      finishOnce(() =>
        finishOAuthPickerStep({ provider: ctx?.platform ?? 'account', fromOAuth: true, navigate })
      ),
    onFailed: handleFailed,
  })
  const { resume } = connect

  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    if (!ctx) {
      handleFailed()
      return
    }
    void resume(ctx)
  }, [ctx, resume, handleFailed])

  // Closing the picker or declining the account switch ends the connect.
  const dialogConnect = useMemo(
    () => ({
      ...connect,
      closePicker: () => {
        connect.closePicker()
        handleCancelled()
      },
      cancelMismatch: () => {
        connect.cancelMismatch()
        handleCancelled()
      },
    }),
    [connect, handleCancelled]
  )

  return (
    <div className="flex h-full min-h-[320px] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      <GatewayConnectDialogs connect={dialogConnect} />
    </div>
  )
}
